<script setup>
/**
 * "Set by {hub}" (XN-4561, story AC 2): what the agency holds about this
 * person and only the agency changes — their policies, how long they've been
 * with it, the reference both sides quote, and who to ask. Prototype
 * `profile()`, build 30 Sep 12:40.
 */
import { computed } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { differenceInYears } from 'date-fns';
import XlAvatar from '../xl/XlAvatar.vue';
import XlButton from '../xl/XlButton.vue';
import { longDate } from '../../helpers/coverage';
import { toLocalDate } from '../../helpers/usDate';

const props = defineProps({
  setBy: { type: Object, required: true },
  hub: { type: String, required: true },
  // Who the line under the card says to message: the broker's first name, "your broker", or the hub.
  messageWho: { type: String, required: true },
});
const router = useRouter();

// A broker SSO cannot name is still their broker: never the no-broker row,
// and no initials made up from a placeholder.
const contact = computed(() => {
  const broker = props.setBy.broker ?? null;
  if (!broker) return { label: 'Your contact', name: props.hub, initials: props.hub };
  return { label: 'Your broker', name: broker.name || 'Your broker', initials: broker.name || '', photo: broker.photo || '' };
});

const since = computed(() => {
  const ymd = props.setBy.with_us_since;
  if (!ymd) return null;
  const years = differenceInYears(new Date(), toLocalDate(ymd));
  return { date: longDate(ymd), years: years >= 1 ? `${years} ${years === 1 ? 'year' : 'years'}` : '' };
});
</script>

<template>
  <section data-testid="details-card-set-by">
    <div class="pdet-set__sh"><h2>Set by {{ hub }}</h2><i class="pdet-set__ln" /></div>

    <dl class="pdet-set__card">
      <div class="pdet-set__row" data-testid="details-coverage">
        <dt>Your coverage</dt>
        <dd v-if="!setBy.coverage" class="pdet-set__quiet">Couldn't load your policies just now.</dd>
        <dd v-else-if="!setBy.coverage.length" class="pdet-set__quiet">No policies on record yet</dd>
        <dd v-else class="pdet-set__tags">
          <RouterLink
            v-for="policy in setBy.coverage"
            :key="policy.id"
            :to="`/hub/coverage/${policy.id}`"
            class="xl-tag xl-tag--brand"
          >{{ policy.title }}</RouterLink>
        </dd>
      </div>

      <div v-if="since" class="pdet-set__row" data-testid="details-since">
        <dt>With us since</dt>
        <dd>{{ since.date }}<span v-if="since.years" class="pdet-set__quiet"> · {{ since.years }}</span></dd>
      </div>

      <div v-if="setBy.customer_reference" class="pdet-set__row" data-testid="details-reference">
        <dt>Customer reference</dt>
        <dd>{{ setBy.customer_reference }}</dd>
      </div>

      <div class="pdet-set__row" data-testid="details-contact">
        <dt>{{ contact.label }}</dt>
        <dd class="pdet-set__who">
          <XlAvatar :name="contact.initials" :src="contact.photo || ''" :size="30" class="pdet-set__av" />
          <b>{{ contact.name }}</b>
          <XlButton variant="ghost" size="sm" class="pdet-set__msg" @click="router.push('/hub/messages')">Message</XlButton>
        </dd>
      </div>
    </dl>

    <p class="pdet-set__after">To change any of these, message {{ messageWho }}.</p>
  </section>
</template>

<style scoped>
/* Measurements from the prototype's profile() Set by card and .row, build 30 Sep 12:40. */
.pdet-set__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.pdet-set__sh h2 { margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xl-color-outline-variant); }
.pdet-set__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.pdet-set__card {
  padding: 8px 22px;
  margin: 0 0 14px;
  background: #fff;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-1);
}
.pdet-set__row { display: flex; align-items: center; gap: 14px; padding: 15px 0; border-bottom: 1px solid var(--xl-color-border-subtle); }
.pdet-set__row:last-child { border-bottom: 0; }
.pdet-set__row dt { width: 180px; flex: 0 0 180px; font-size: 14px; color: var(--xl-color-outline); }
.pdet-set__row dd { margin: 0; flex: 1; min-width: 0; font-weight: 500; overflow-wrap: anywhere; }
.pdet-set__row .pdet-set__quiet { font-weight: 400; color: var(--xl-color-outline); }
.pdet-set__tags { display: flex; gap: 7px; flex-wrap: wrap; }
.pdet-set__tags a { max-width: 100%; overflow: hidden; text-overflow: ellipsis; text-decoration: none; cursor: pointer; }
.pdet-set__tags a:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.pdet-set__who { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pdet-set__who b { font-weight: 500; }
.pdet-set__who .pdet-set__av { background: linear-gradient(150deg, #8154A6, var(--xl-color-primary)); color: #fff; }
.pdet-set__msg { margin-left: auto; }
.pdet-set__after { margin: 12px 0 0; font-size: 13px; color: var(--xl-color-outline); }
@media (max-width: 480px) {
  .pdet-set__card { padding: 4px 14px; }
  .pdet-set__row { flex-direction: column; align-items: flex-start; gap: 4px; }
  .pdet-set__row dt { width: auto; flex: none; }
  .pdet-set__row dd { width: 100%; }
}
</style>
